import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from "mobx";
import { inject, injectable } from "tsyringe";
import {
  type DeletedBoxItem,
  type DeletedProductItem,
  type IWarehouseApi,
  type SoftDeleteAuditItem,
  WAREHOUSE_API_TOKEN,
} from "../../api/WarehouseApi";
import { ViewModel } from "../../lib/vvm";
import {
  NAVIGATION_STORE_TOKEN,
  type INavigationStore,
  Screens,
} from "../../stores/navigation";
import { getStoredUser } from "../../utils/authStorage";

type DeleteCandidate =
  | { entity: "product"; item: DeletedProductItem }
  | { entity: "box"; item: DeletedBoxItem }
  | null;

function safeText(value: string | null): string {
  return value && value.trim() ? value.trim() : "без комментария";
}

function formatDeletedAt(value: string | null): string {
  if (!value) {
    return "дата неизвестна";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

@injectable()
export class DeletedEntitiesPageViewModel extends ViewModel {
  @observable.shallow
  deletedProducts: DeletedProductItem[] = [];

  @observable.shallow
  deletedBoxes: DeletedBoxItem[] = [];

  @observable.ref
  private productAuditById: Record<string, SoftDeleteAuditItem> = {};

  @observable.ref
  private boxAuditById: Record<string, SoftDeleteAuditItem> = {};

  @observable
  isLoading = false;

  @observable
  error: string | null = null;

  @observable
  processingKey: string | null = null;

  @observable.ref
  deleteCandidate: DeleteCandidate = null;

  constructor(
    @inject(WAREHOUSE_API_TOKEN) private readonly api: IWarehouseApi,
    @inject(NAVIGATION_STORE_TOKEN)
    private readonly navigationStore: INavigationStore,
  ) {
    super();
    makeObservable(this);
  }

  @computed
  get isAdmin(): boolean {
    return getStoredUser()?.role === "admin";
  }

  @computed
  get deletedProductsCount(): number {
    return this.deletedProducts.length;
  }

  @computed
  get deletedBoxesCount(): number {
    return this.deletedBoxes.length;
  }

  @computed
  get totalDeleted(): number {
    return this.deletedProductsCount + this.deletedBoxesCount;
  }

  @computed
  get isConfirmOpen(): boolean {
    return Boolean(this.deleteCandidate);
  }

  @computed
  get confirmTitle(): string {
    if (!this.deleteCandidate) {
      return "Уничтожить навсегда?";
    }

    const label =
      this.deleteCandidate.entity === "product" ? "товар" : "коробку";
    return `Уничтожить ${label} "${this.deleteCandidate.item.name}"?`;
  }

  @computed
  get confirmDescription(): string {
    if (!this.deleteCandidate) {
      return "";
    }

    const { item, entity } = this.deleteCandidate;
    return [
      `ID: ${item.id}`,
      `Тип: ${entity === "product" ? "товар" : "коробка"}`,
      `Удалено: ${formatDeletedAt(item.deletedAt)}`,
      "Это действие необратимо.",
    ].join("\n");
  }

  protected onViewMounted(): void {
    void this.refresh();
  }

  @action.bound
  goBack(): void {
    this.navigationStore.setScreen(Screens.Main);
  }

  @action.bound
  isProcessing(entity: "product" | "box", id: string): boolean {
    return this.processingKey === `${entity}:${id}`;
  }

  @action.bound
  requestHardDeleteProduct(item: DeletedProductItem): void {
    if (this.processingKey) {
      return;
    }

    this.deleteCandidate = { entity: "product", item };
  }

  @action.bound
  requestHardDeleteBox(item: DeletedBoxItem): void {
    if (this.processingKey) {
      return;
    }

    this.deleteCandidate = { entity: "box", item };
  }

  @action.bound
  cancelHardDelete(): void {
    this.deleteCandidate = null;
  }

  @action.bound
  async confirmHardDelete(): Promise<void> {
    if (!this.deleteCandidate || this.processingKey) {
      return;
    }

    const { entity, item } = this.deleteCandidate;
    const key = `${entity}:${item.id}`;
    this.processingKey = key;
    this.error = null;

    try {
      if (entity === "product") {
        await this.api.hardDeleteProduct(item.id);
      } else {
        await this.api.hardDeleteBox(item.id);
      }

      runInAction(() => {
        this.deleteCandidate = null;
      });

      await this.refresh();
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error
            ? error.message
            : "Не удалось удалить сущность окончательно";
      });
    } finally {
      runInAction(() => {
        this.processingKey = null;
      });
    }
  }

  @action.bound
  async restoreProduct(item: DeletedProductItem): Promise<void> {
    if (this.processingKey) {
      return;
    }

    this.processingKey = `product:${item.id}`;
    this.error = null;

    try {
      await this.api.restoreProduct(item.id);
      await this.refresh();
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error
            ? error.message
            : "Не удалось восстановить товар";
      });
    } finally {
      runInAction(() => {
        this.processingKey = null;
      });
    }
  }

  @action.bound
  async restoreBox(item: DeletedBoxItem): Promise<void> {
    if (this.processingKey) {
      return;
    }

    this.processingKey = `box:${item.id}`;
    this.error = null;

    try {
      await this.api.restoreBox(item.id);
      await this.refresh();
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error
            ? error.message
            : "Не удалось восстановить коробку";
      });
    } finally {
      runInAction(() => {
        this.processingKey = null;
      });
    }
  }

  @action.bound
  async refresh(): Promise<void> {
    if (!this.isAdmin) {
      this.error = "Раздел доступен только администратору";
      this.deletedProducts = [];
      this.deletedBoxes = [];
      this.productAuditById = {};
      this.boxAuditById = {};
      return;
    }

    this.isLoading = true;
    this.error = null;

    try {
      const [products, boxes, audit] = await Promise.all([
        this.api.getDeletedProducts({ limit: 200, offset: 0 }),
        this.api.getDeletedBoxes({ limit: 200, offset: 0 }),
        this.api.getSoftDeleteAudit({ limit: 400, offset: 0 }),
      ]);

      const { productAuditById, boxAuditById } = this.buildAuditMaps(
        audit.items,
      );

      runInAction(() => {
        this.deletedProducts = products.items;
        this.deletedBoxes = boxes.items;
        this.productAuditById = productAuditById;
        this.boxAuditById = boxAuditById;
      });
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error
            ? error.message
            : "Не удалось загрузить удаленные сущности";
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  formatDeletedAt = formatDeletedAt;
  safeText = safeText;

  @action.bound
  getProductAudit(itemId: string): SoftDeleteAuditItem | null {
    return this.productAuditById[itemId] || null;
  }

  @action.bound
  getBoxAudit(itemId: string): SoftDeleteAuditItem | null {
    return this.boxAuditById[itemId] || null;
  }

  @action.bound
  formatAuditActor(audit: SoftDeleteAuditItem | null): string {
    if (!audit) {
      return "неизвестно";
    }
    if (audit.actorUserName && audit.actorUserId) {
      return `${audit.actorUserName} (${audit.actorUserId})`;
    }
    if (audit.actorUserName) {
      return audit.actorUserName;
    }
    if (audit.actorUserId) {
      return audit.actorUserId;
    }
    return "система";
  }

  @action.bound
  formatAuditAt(audit: SoftDeleteAuditItem | null): string {
    if (!audit) {
      return "дата неизвестна";
    }

    return formatDeletedAt(audit.createdAt);
  }

  @action.bound
  formatAuditSource(audit: SoftDeleteAuditItem | null): string {
    if (!audit || !audit.source) {
      return "не указан";
    }

    return audit.source;
  }

  private buildAuditMaps(items: SoftDeleteAuditItem[]): {
    productAuditById: Record<string, SoftDeleteAuditItem>;
    boxAuditById: Record<string, SoftDeleteAuditItem>;
  } {
    const productAuditById: Record<string, SoftDeleteAuditItem> = {};
    const boxAuditById: Record<string, SoftDeleteAuditItem> = {};

    for (const item of items) {
      if (!item.entityId) {
        continue;
      }

      if (item.entityType === "product") {
        if (!productAuditById[item.entityId]) {
          productAuditById[item.entityId] = item;
        }
        continue;
      }

      if (!boxAuditById[item.entityId]) {
        boxAuditById[item.entityId] = item;
      }
    }

    return { productAuditById, boxAuditById };
  }
}
